// filepath: /home/toofoocustomer/tf-node/tf_customer_web/components/customer/CustomerCoreProfilePanel.tsx

"use client";

import { FormEvent, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import {
  type CustomerCoreProfile,
  customerIdentityErrorMessage,
  getCustomerCoreProfile,
  updateCustomerCoreProfile,
  updateCustomerPrimaryPhone,
} from "@/lib/customer/identity-api";
import {
  ISO_REGION_CODES,
  SUPPORTED_CUSTOMER_LOCALES,
  regionDisplayName,
} from "@/lib/customer/global-profile-options";
import {
  DEFAULT_PHONE_REGION_CODE,
  composeE164PhoneNumber,
  formatPhoneNationalInput,
  phoneCallingCode,
  phoneCountryOptions,
  phoneNationalInputFromE164,
} from "@/lib/customer/phone-number";

const HYDRATION_SUBSCRIBE = () => () => {};
const HYDRATION_CLIENT_SNAPSHOT = () => true;
const HYDRATION_SERVER_SNAPSHOT = () => false;

export function CustomerCoreProfilePanel() {
  const [profile, setProfile] = useState<CustomerCoreProfile | null>(null);
  const [legalName, setLegalName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [sex, setSex] = useState<"" | "MALE" | "FEMALE">("");
  const [displayName, setDisplayName] = useState("");
  const [preferredLocale, setPreferredLocale] = useState("en-US");
  const [homeRegion, setHomeRegion] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneRegionCode, setPhoneRegionCode] = useState(DEFAULT_PHONE_REGION_CODE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const regionLabelsReady = useSyncExternalStore(
    HYDRATION_SUBSCRIBE,
    HYDRATION_CLIENT_SNAPSHOT,
    HYDRATION_SERVER_SNAPSHOT,
  );

  const regionOptions = useMemo(() => {
    if (!regionLabelsReady) {
      return ISO_REGION_CODES.map((code) => ({ code, label: code }));
    }
    const locale = preferredLocale || "en-US";
    return ISO_REGION_CODES
      .map((code) => ({ code, label: regionDisplayName(code, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [preferredLocale, regionLabelsReady]);
  const phoneOptions = useMemo(
    () => phoneCountryOptions("ko-KR", regionLabelsReady),
    [regionLabelsReady],
  );

  function apply(next: CustomerCoreProfile) {
    setProfile(next);
    setLegalName(next.legal_name ?? "");
    setBirthDate(next.birth_date ?? "");
    setSex(next.sex ?? "");
    setDisplayName(next.display_name ?? "");
    setPreferredLocale(next.preferred_locale ?? "en-US");
    setHomeRegion(next.home_region ?? "");
    const nextPhoneRegion = phoneCallingCode(next.phone?.region_code)
      ? (next.phone?.region_code ?? DEFAULT_PHONE_REGION_CODE)
      : DEFAULT_PHONE_REGION_CODE;
    setPhoneRegionCode(nextPhoneRegion);
    setPhoneNumber(phoneNationalInputFromE164(nextPhoneRegion, next.phone?.value));
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await getCustomerCoreProfile();
        if (!cancelled) apply(result);
      } catch (error) {
        if (!cancelled) setErrorMessage(customerIdentityErrorMessage(error, "profile"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    setErrorMessage("");

    const normalizedPhone = phoneNumber.trim()
      ? composeE164PhoneNumber(phoneRegionCode, phoneNumber)
      : null;
    if (phoneNumber.trim() && !normalizedPhone) {
      setErrorMessage("휴대폰 국가번호와 번호를 확인해 주세요.");
      return;
    }

    setSaving(true);
    try {
      let next = await updateCustomerCoreProfile({
        legal_name: legalName.trim() || null,
        birth_date: birthDate || null,
        sex: sex || null,
        display_name: displayName.trim() || null,
        preferred_locale: preferredLocale.trim() || null,
        home_region: homeRegion || null,
      });
      if (normalizedPhone) {
        next = await updateCustomerPrimaryPhone({
          phone_number: normalizedPhone,
          region_code: phoneRegionCode,
        });
      }
      apply(next);
      setMessage("기본 정보가 저장되었습니다.");
    } catch (error) {
      setErrorMessage(customerIdentityErrorMessage(error, "profile"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="profile-panel" data-e2e="customer-core-profile-loading">기본 정보를 확인하고 있습니다.</div>;
  }

  return (
    <section className="profile-panel" data-e2e="customer-core-profile-panel">
      <div className="profile-panel-heading">
        <div>
          <p className="eyebrow">CUSTOMER CORE PROFILE</p>
          <h2>기본 정보</h2>
        </div>
        <span className={`verification-badge ${profile?.identity_verification?.verified ? "is-verified" : ""}`}>
          {profile?.identity_verification?.verified ? "본인확인 완료" : "본인확인 미완료"}
        </span>
      </div>

      <form className="auth-form" onSubmit={submit}>
        <div className="profile-inline-fields">
          <div className="form-field">
            <label htmlFor="account-legal-name">실명</label>
            <input
              id="account-legal-name"
              value={legalName}
              onChange={(event) => setLegalName(event.target.value)}
              maxLength={160}
              autoComplete="name"
            />
          </div>
          <div className="form-field">
            <label htmlFor="account-birth-date">생년월일</label>
            <input
              id="account-birth-date"
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              autoComplete="bday"
            />
          </div>
        </div>
        <p className="field-help">실명과 생년월일은 수정할 수 있습니다. 본인확인 완료 후 값을 변경하면 다시 미확인 상태가 됩니다.</p>

        <div className="form-field">
          <label htmlFor="account-sex">성별</label>
          <select
            id="account-sex"
            value={sex}
            onChange={(event) => setSex(event.target.value as "" | "MALE" | "FEMALE")}
          >
            <option value="">선택 안 함</option>
            <option value="MALE">남성</option>
            <option value="FEMALE">여성</option>
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="account-display-name">표시 이름</label>
          <input
            id="account-display-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={160}
            autoComplete="name"
          />
        </div>

        <div className="profile-inline-fields">
          <div className="form-field">
            <label htmlFor="account-home-region">국가/지역</label>
            <select
              id="account-home-region"
              value={homeRegion}
              onChange={(event) => setHomeRegion(event.target.value)}
            >
              <option value="">선택 안 함</option>
              {regionOptions.map((item) => (
                <option key={item.code} value={item.code}>{item.label} ({item.code})</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="account-locale">기본 언어</label>
            <select
              id="account-locale"
              value={preferredLocale}
              onChange={(event) => setPreferredLocale(event.target.value)}
            >
              {SUPPORTED_CUSTOMER_LOCALES.map((item) => (
                <option key={item.code} value={item.code}>{item.label}</option>
              ))}
            </select>
            <p className="field-help">TOOFOO 계정 안내와 이메일에 사용할 기본 언어입니다.</p>
          </div>
        </div>

        <div className="form-field phone-profile-field">
          <label htmlFor="account-phone">휴대폰 번호</label>
          <div className="phone-entry-row phone-entry-row--profile">
            <select
              id="account-phone-region"
              value={phoneRegionCode}
              onChange={(event) => {
                const nextRegion = event.target.value;
                setPhoneRegionCode(nextRegion);
                setPhoneNumber((current) => formatPhoneNationalInput(nextRegion, current));
              }}
              aria-label="휴대폰 국가번호"
              data-e2e="customer-account-phone-country-code"
            >
              {phoneOptions.map((item) => (
                <option key={item.code} value={item.code}>{item.label}</option>
              ))}
            </select>
            <input
              id="account-phone"
              type="tel"
              inputMode="tel"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(formatPhoneNationalInput(phoneRegionCode, event.target.value))}
              placeholder={phoneRegionCode === "KR" ? "010 1234 5678" : "휴대폰 번호"}
              autoComplete="tel-national"
            />
            <button
              className="secondary-button verification-action-button phone-verification-button"
              type="button"
              disabled
              aria-disabled="true"
              data-e2e="customer-account-phone-identity-verification"
            >
              본인인증
            </button>
          </div>
        </div>
        <p className="field-help">
          대한민국 번호는 010부터 입력해 주세요. 다른 나라 번호는 국가번호를 먼저 선택한 뒤 현지 번호를 입력해 주세요.
        </p>

        {message ? <div className="profile-success" role="status">{message}</div> : null}
        {errorMessage ? <div className="form-error" role="alert">{errorMessage}</div> : null}

        <button className="primary-button" type="submit" disabled={saving}>
          {saving ? "저장 중..." : "기본 정보 저장"}
        </button>
      </form>
    </section>
  );
}
